import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck, Trash2, Filter } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Badge, Select, ConfirmDialog, toast } from '../../components/common';
import { notificationApi } from '../../services/api';
import './Notifications.css';

export default function NotificationList() {
    const queryClient = useQueryClient();
    const [filter, setFilter] = useState('all');
    const [deleteNotif, setDeleteNotif] = useState(null);

    const { data: notifications, isLoading } = useQuery({
        queryKey: ['notifications', filter],
        queryFn: () => notificationApi.list({ isRead: filter === 'all' ? undefined : filter === 'unread' ? false : true }),
        select: (res) => res.data?.data || []
    });

    const markAsReadMutation = useMutation({
        mutationFn: (id) => notificationApi.markAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['notifications']);
        }
    });

    const markAllAsReadMutation = useMutation({
        mutationFn: () => notificationApi.markAllAsRead(),
        onSuccess: () => {
            toast.success('All notifications marked as read');
            queryClient.invalidateQueries(['notifications']);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => notificationApi.delete(id),
        onSuccess: () => {
            toast.success('Notification deleted');
            queryClient.invalidateQueries(['notifications']);
            setDeleteNotif(null);
        }
    });

    const formatTime = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const now = new Date();
        const diff = now - d;

        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
        return d.toLocaleDateString();
    };

    const getIcon = (type) => {
        const icons = {
            'approval': '👍',
            'rejection': '❌',
            'reminder': '⏰',
            'system': 'ℹ️',
            'alert': '⚠️'
        };
        return icons[type] || '📬';
    };

    const filterOptions = [
        { value: 'all', label: 'All Notifications' },
        { value: 'unread', label: 'Unread' },
        { value: 'read', label: 'Read' }
    ];

    const unreadCount = notifications?.filter(n => !n.isRead).length || 0;

    return (
        <div className="notifications-page">
            <PageHeader
                title="Notifications"
                subtitle={`${unreadCount} unread notifications`}
                breadcrumbs={[{ label: 'Notifications', path: '/notifications' }]}
                actions={
                    <Button
                        variant="secondary"
                        icon={CheckCheck}
                        onClick={() => markAllAsReadMutation.mutate()}
                        disabled={unreadCount === 0}
                    >
                        Mark All as Read
                    </Button>
                }
            />

            <Card>
                <div className="notifications-toolbar">
                    <Select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        options={filterOptions}
                        className="notif-filter"
                    />
                </div>

                <div className="notifications-list">
                    {isLoading ? (
                        <div className="notif-loading">Loading notifications...</div>
                    ) : notifications.length === 0 ? (
                        <div className="notif-empty">
                            <Bell size={48} />
                            <p>No notifications</p>
                        </div>
                    ) : (
                        notifications.map(notif => (
                            <div
                                key={notif.id}
                                className={`notification-item ${!notif.isRead ? 'unread' : ''}`}
                                onClick={() => !notif.isRead && markAsReadMutation.mutate(notif.id)}
                            >
                                <div className="notif-icon">{getIcon(notif.type)}</div>
                                <div className="notif-content">
                                    <div className="notif-title">{notif.title}</div>
                                    <div className="notif-message">{notif.message}</div>
                                    <div className="notif-time">{formatTime(notif.createdAt)}</div>
                                </div>
                                <div className="notif-actions">
                                    {!notif.isRead && (
                                        <button
                                            className="notif-action-btn"
                                            onClick={(e) => { e.stopPropagation(); markAsReadMutation.mutate(notif.id); }}
                                            title="Mark as read"
                                        >
                                            <Check size={16} />
                                        </button>
                                    )}
                                    <button
                                        className="notif-action-btn delete"
                                        onClick={(e) => { e.stopPropagation(); setDeleteNotif(notif); }}
                                        title="Delete"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </Card>

            <ConfirmDialog
                isOpen={!!deleteNotif}
                onClose={() => setDeleteNotif(null)}
                onConfirm={() => deleteMutation.mutate(deleteNotif?.id)}
                title="Delete Notification"
                message="Are you sure you want to delete this notification?"
                confirmText="Delete"
                variant="danger"
                loading={deleteMutation.isPending}
            />
        </div>
    );
}
